import React, { useState, useRef } from "react";
import {
  Box,
  Text,
  Flex,
  Button,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  SimpleGrid,
  useToast,
  Icon,
} from "@chakra-ui/react";
import { FaUser, FaUserSecret } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstance from "../../../routes/axiosInstance";

const BroadcastScreen = ({
  space,
  admin,
  member,
  activities,
  referenceId,
  fetchSpace,
}) => {
  const navigate = useNavigate();
  const initialReactions = {};
  if (activities && Array.isArray(activities.reactions)) {
    activities.reactions.forEach(({ emoji, count }) => {
      initialReactions[emoji] = count;
    });
  } else {
    Object.assign(initialReactions, {
      "👍": 0,
      "❤️": 0,
      "😂": 0,
      "🎉": 0,
      "👏": 0,
      "😮": 0,
      "😡": 0,
    });
  }
  const emojis = Object.keys(initialReactions);
  const initialUserReaction =
    member?.activity?.activityType === "reaction" && member.activity.emoji
      ? member.activity.emoji
      : null;
  const [reactions, setReactions] = useState(initialReactions);
  const [userReaction, setUserReaction] = useState(initialUserReaction);
  const [isMemberPopoverOpen, setIsMemberPopoverOpen] = useState(false);
  const [isEmojiPopoverOpen, setIsEmojiPopoverOpen] = useState(false);
  const memberBtnRef = useRef();
  const toast = useToast();

  const sendReactionActivity = async (emoji) => {
    try {
      const activityRes = await axiosInstance.post(
        `/api/spaces/${referenceId}/activity`,
        {
          activityType: "reaction",
          emoji,
        },
        {
          headers: {
            "Content-type": "application/json",
            Authorization: member?.guestAccessId
              ? `Guest ${member.guestAccessId}`
              : undefined,
          },
        },
      );
      if (activityRes.status !== 201) {
        navigate(`/space/${referenceId}/preview`);
        return false;
      }
      if (typeof fetchSpace === "function") {
        fetchSpace();
      }
    } catch (err) {
      navigate(`/space/${referenceId}/preview`);
      toast({
        title: "Failed to record reaction",
        description: "Redirecting to space preview...",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const handleReact = (emoji) => {
    if (userReaction && userReaction !== emoji) {
      setReactions((prev) => ({
        ...prev,
        [userReaction]: Math.max((prev[userReaction] || 1) - 1, 0),
      }));
    }
    setReactions((prev) => ({ ...prev, [emoji]: (prev[emoji] || 0) + 1 }));
    setUserReaction(emoji);
    sendReactionActivity(emoji);
  };

  const handleCopyGuestId = () => {
    if (member?.guestAccessId) {
      navigator.clipboard.writeText(member.guestAccessId);
      toast({
        title: "Guest Access ID copied!",
        description: "You can use this ID for future login.",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const initialEmojiList = ["👍", "❤️", "😂", "🎉", "👏", "😮", "😡"];
  const plusEmojis = initialEmojiList;

  return (
    <Flex
      direction="column"
      align="center"
      minH="100vh"
      bgGradient="linear(to-br, teal.600, white, teal.50)"
    >
      {/* Content area */}
      <Flex direction="column" align="center" flex="1" w="100%" p={4}>
        <Box
          p={0}
          maxW="md"
          w="100%"
          bg="white"
          borderWidth="1px"
          borderRadius="lg"
          boxShadow="md"
          mt={4}
          overflow="hidden"
        >
          {/* Header section - Facebook style */}
          <Box p={4} borderBottom="1px solid" borderColor="gray.200">
            <Flex align="center" justify="space-between">
              <Flex align="center" flex="1">
                <Avatar
                  name={admin?.name || admin?.alias || "Unknown User"}
                  size="md"
                  bg="teal.500"
                  color="white"
                />
                <Box ml={3} flex="1">
                  <Flex align="center">
                    <Text
                      fontWeight="semibold"
                      fontSize="md"
                      color="gray.800"
                      noOfLines={1}
                    >
                      {admin?.name || admin?.alias || "Unknown User"}
                    </Text>
                    <Icon
                      as={space.isIncognito ? FaUserSecret : FaUser}
                      ml={2}
                      color="gray.500"
                      size="sm"
                      title={space.isIncognito ? "Incognito" : "Admin"}
                    />
                  </Flex>
                  <Text fontSize="xs" color="gray.500">
                    {space.createdAt
                      ? dayjs(space.createdAt).format("MMM D, YYYY h:mm A")
                      : ""}
                  </Text>
                </Box>
              </Flex>

              {/* Member info */}
              <Popover
                isOpen={isMemberPopoverOpen}
                onClose={() => setIsMemberPopoverOpen(false)}
                placement="bottom-end"
                initialFocusRef={memberBtnRef}
              >
                <PopoverTrigger>
                  <Flex
                    align="center"
                    cursor="pointer"
                    ref={memberBtnRef}
                    onClick={() => setIsMemberPopoverOpen(true)}
                    px={2}
                    py={1}
                    borderRadius="md"
                    _hover={{ bg: "gray.100" }}
                  >
                    <FaUser size={14} color="#718096" />
                    <Text
                      ml={2}
                      fontWeight="medium"
                      fontSize="sm"
                      color="gray.700"
                      noOfLines={1}
                    >
                      {member?.name || member?.alias || "You"}
                    </Text>
                  </Flex>
                </PopoverTrigger>
                <PopoverContent w="220px">
                  <PopoverBody>
                    <Text fontWeight="semibold" mb={2}>
                      {member?.name || member?.alias || "You"}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Guest Access ID:
                    </Text>
                    <Flex align="center" mb={2}>
                      <Text fontSize="xs" color="gray.700" isTruncated>
                        {member?.guestAccessId || "-"}
                      </Text>
                      <Button ml={2} size="xs" onClick={handleCopyGuestId}>
                        Copy
                      </Button>
                    </Flex>
                    <Text fontSize="xs" color="gray.400">
                      Copy this ID for further login
                    </Text>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>
          </Box>

          {/* Content section */}
          <Box p={4}>
            <Text fontWeight="semibold" fontSize="lg" mb={3} color="gray.800">
              {space.title || "Broadcast"}
            </Text>
            <Text mb={6} fontSize="md" color="gray.700" lineHeight="1.6">
              {space.metaData?.content || "No announcement."}
            </Text>
          </Box>

          {/* Reactions section */}
          <Box p={4} borderTop="1px solid" borderColor="gray.200">
            <Text fontWeight="semibold" color="gray.600" mb={3} fontSize="sm">
              React to this announcement:
            </Text>
            <Flex justify="center" gap={2} align="center" flexWrap="wrap">
              {emojis.map((emoji) => (
                <Button
                  key={emoji}
                  onClick={() => handleReact(emoji)}
                  size="sm"
                  variant={userReaction === emoji ? "solid" : "outline"}
                  colorScheme={userReaction === emoji ? "teal" : "gray"}
                  borderRadius="full"
                  px={3}
                  py={2}
                  h="auto"
                  minW="fit-content"
                  _hover={{
                    transform: "scale(1.05)",
                    boxShadow: "md",
                  }}
                  transition="all 0.2s"
                >
                  <span style={{ fontSize: 16, marginRight: 6 }}>{emoji}</span>
                  <span style={{ fontSize: 13, fontWeight: "bold" }}>
                    {reactions[emoji]}
                  </span>
                </Button>
              ))}
              <Popover
                placement="top"
                isOpen={isEmojiPopoverOpen}
                onClose={() => setIsEmojiPopoverOpen(false)}
              >
                <PopoverTrigger>
                  <Button
                    aria-label="More reactions"
                    onClick={() => setIsEmojiPopoverOpen(true)}
                    size="sm"
                    variant="outline"
                    colorScheme="gray"
                    borderRadius="full"
                    _hover={{
                      transform: "scale(1.05)",
                      boxShadow: "md",
                    }}
                    transition="all 0.2s"
                  >
                    <span style={{ fontSize: 18 }}>➕</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent w="220px">
                  <PopoverBody>
                    <SimpleGrid columns={7} spacing={2}>
                      {plusEmojis.map((emoji) => (
                        <Button
                          key={emoji}
                          onClick={() => handleReact(emoji)}
                          size="sm"
                          variant="ghost"
                          isDisabled={userReaction === emoji}
                          _hover={{
                            bg: "gray.100",
                            transform: "scale(1.1)",
                          }}
                        >
                          <span style={{ fontSize: 18 }}>{emoji}</span>
                        </Button>
                      ))}
                    </SimpleGrid>
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Flex>
          </Box>
        </Box>
      </Flex>

      {/* Footer - Always at bottom */}
      <Box w="100%" textAlign="center" py={4}>
        <Text fontSize="xs" color="gray.500" mb={2}>
          This content is neither created nor endorsed by Vartaverse
        </Text>
        <Text
          fontSize="lg"
          fontWeight="bold"
          color="teal.600"
          cursor="pointer"
          _hover={{
            color: "teal.700",
            textDecoration: "none",
          }}
          onClick={() => window.open(window.location.origin, "_blank")}
        >
          VartaVerse
        </Text>
      </Box>
    </Flex>
  );
};

export default BroadcastScreen;
